/**
 * Field tooltip configuration.
 *
 * Provides input guidance tooltips for form fields across all item types.
 * Tooltips are resolved by key in the following priority:
 *   1. "{ItemType}.{fieldName}" — item-type-specific tooltip
 *   2. "{fieldName}" — global tooltip (applies to all item types)
 *
 * Each entry supports vi/en translations.
 *
 * To add a new tooltip, simply add an entry below.
 */

interface FieldTooltipEntry {
  vi: string;
  en: string;
}

const fieldTooltips: Record<string, FieldTooltipEntry> = {
  // ──── Class ────────────────────────────────────────────
  "Class.scheduleDay": {
    vi: "Nhập ngày học trong tuần, ví dụ: Thứ 2, Thứ 4, Thứ 6",
    en: "Enter study days, e.g.: Monday, Wednesday, Friday",
  },
  "Class.scheduleTime": {
    vi: "Nhập khung giờ học, ví dụ: 8:00 - 9:30",
    en: "Enter class hours, e.g.: 8:00 - 9:30",
  },
};

/**
 * Get the tooltip text for a form field.
 *
 * @param fieldName  - camelCase mapped field name (e.g. "scheduleDay")
 * @param itemType   - Aras ItemType name (e.g. "Class")
 * @param language   - current UI language ("vi" | "en")
 * @returns tooltip string or undefined if none configured
 */
export function getFieldTooltip(
  fieldName: string,
  itemType: string | undefined,
  language: string,
): string | undefined {
  const lang = language === "en" ? "en" : "vi";

  // 1. Item-type-specific
  if (itemType) {
    const entry = fieldTooltips[`${itemType}.${fieldName}`];
    if (entry) return entry[lang];
  }

  // 2. Global fallback
  const global = fieldTooltips[fieldName];
  if (global) return global[lang];

  return undefined;
}
